import React, { useEffect } from 'react';

import { TextInput } from '@components/admin/Forms';
import { calendarStrings } from '@data/calendar';
import { CurrencyRate } from '@models/CurrencyRate';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createValidate, positiveNumberField } from '@utils/forms';
import { toJST } from '@utils/types';
import clsx from 'clsx';
import { Form, Formik, useFormikContext } from 'formik';
import * as t from 'io-ts';

const useStyles = makeMuiStyles((theme, common) => ({
  calculatorContainer: {
    ...common.flexColumn,
    ...common.middleText,
    flex: 1,
    border: `1px solid ${theme.palette.grey[300]}`,
    padding: '1rem',
  },
  calculatorDisplay: {
    ...common.flexColumn,
    ...common.fontWeight500,
    rowGap: '1rem',
  },
  exchangeRateDisplay: {
    ...common.middleText,
    ...common.flexInline,
    ...common.fontWeight500,
    alignItems: 'flex-end',
  },
  exchangeRateDisplayFirst: {
    flex: 1,
    ...common.textAlignRight,
  },
  exchangeRateDisplaySecond: {
    flex: 3,
    ...common.textAlignRight,
  },
  rateTime: {
    ...common.exSmallerText,
    ...common.fontWeight500,
    marginTop: 16,
    marginBottom: 32,
  },
  calculatorContent: {
    ...common.flexColumn,
    rowGap: 10,
  },
  calculatorContentRow: {
    ...common.flexInline,
    alignItems: 'flex-start',
    columnGap: 11,
  },
  firstExchangeRoot: {
    flex: 1,
  },
  secondExchangeRoot: {
    width: 80,
  },
  secondExchangeTextInput: {
    width: '0px !important',
  },
  firstExchangeTextInput: {
    width: '0px !important',
  },
}));

interface ExchangeRateDisplayProps {
  firstAmount: number;
  firstExchange: string;
  secondAmount: number;
  secondExchange: string;
}

const ExchangeRateDisplay: React.FC<ExchangeRateDisplayProps> = (props) => {
  const { firstAmount, firstExchange, secondAmount, secondExchange } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.exchangeRateDisplay}>
      <div className={classes.exchangeRateDisplayFirst}>
        {firstAmount}
        {firstExchange}=
      </div>
      <div className={classes.exchangeRateDisplaySecond}>
        <span className={classes.largeText} style={{ lineHeight: 0.6 }}>
          {secondAmount.toFixed(2)}
        </span>{' '}
        {secondExchange}
      </div>
    </div>
  );
};

const ExchangeAutoFill: React.FC<{ rate: number }> = (props) => {
  const { rate } = props;

  const formik = useFormikContext<t.TypeOf<typeof exchangeForm>>();
  useEffect(() => {
    const values = { ...formik.values };

    const realRate = values.firstExchangeType === 'JPY' ? rate : 1 / rate;

    if (values.focused === 'firstExchange') {
      formik.setFieldValue('secondExchange', (realRate * Number(values.firstExchange)).toFixed(2));
    } else {
      formik.setFieldValue('firstExchange', (Number(values.secondExchange) / realRate).toFixed(2));
    }
  }, [...Object.values(formik.values), rate]);

  return null;
};

export interface ExchangeCalendarProps {
  date: Date;
  item: CurrencyRate;
}

const exchangeForm = t.type({
  firstExchange: positiveNumberField(),
  firstExchangeType: t.string,
  secondExchange: positiveNumberField(),
  focused: t.string,
});

export const ExchangeCalculator: React.FC<ExchangeCalendarProps> = (props) => {
  const { date, item } = props;

  const rate = 1 / item.rate;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <Formik<t.TypeOf<typeof exchangeForm>>
      initialValues={{
        firstExchange: 100,
        firstExchangeType: 'JPY',
        secondExchange: undefined,
        focused: 'firstExchange',
      }}
      validate={createValidate(exchangeForm)}
      onSubmit={() => {}}
    >
      {({ values, setFieldValue }) => (
        <Form className={classes.calculatorContainer}>
          <ExchangeAutoFill rate={rate} />
          <div className={classes.calculatorDisplay}>
            {calendarStrings.yearMonthDayWeekday(date)}
            <ExchangeRateDisplay
              firstAmount={1}
              firstExchange="USD"
              secondAmount={1 / rate}
              secondExchange="JPY"
            />
            <ExchangeRateDisplay
              firstAmount={100}
              firstExchange="JPY"
              secondAmount={rate * 100}
              secondExchange="USD"
            />

            <div className={classes.rateTime}>{toJST(new Date(item.update_time))}</div>
          </div>

          <div className={classes.calculatorContent}>
            <div className={classes.calculatorContentRow}>
              <TextInput
                name="firstExchange"
                type="number"
                noLabel
                formClasses={{
                  root: classes.firstExchangeRoot,
                }}
                classes={{
                  input: clsx(
                    classes.firstExchangeTextInput,
                    values.focused !== 'firstExchange' && classes.textGray500,
                  ),
                }}
                onFocus={() => setFieldValue('focused', 'firstExchange')}
              />

              <TextInput
                name="firstExchangeType"
                choose
                noLabel
                options={[
                  { value: 'JPY', label: 'JPY' },
                  { value: 'USD', label: 'USD' },
                ]}
                formClasses={{
                  root: classes.secondExchangeRoot,
                }}
                classes={{
                  input: classes.secondExchangeTextInput,
                }}
              />
            </div>

            <div className={classes.calculatorContentRow}>
              <TextInput
                name="secondExchange"
                type="number"
                noLabel
                formClasses={{
                  root: classes.firstExchangeRoot,
                }}
                classes={{
                  input: clsx(
                    classes.firstExchangeTextInput,
                    values.focused !== 'secondExchange' && classes.textGray500,
                  ),
                }}
                onFocus={() => setFieldValue('focused', 'secondExchange')}
              />

              <TextInput
                name="firstExchangeType"
                choose
                noLabel
                options={[
                  { value: 'USD', label: 'JPY' },
                  { value: 'JPY', label: 'USD' },
                ]}
                formClasses={{
                  root: classes.secondExchangeRoot,
                }}
                classes={{
                  input: classes.secondExchangeTextInput,
                }}
              />
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};
